const { sortBy, last, omit, pick, get, isEmpty, } = require('lodash');
const { isURL } = require('validator');

const { leftTrialDays } = require('../util');
const { budgetSubjectTypes, allDimensions, } = require('../config');
const { accountItemCategoriesByName, } = require('../category');

const { entries } = Object;
const plans = {
  budget: {
    label: '予実管理',
    maxBudgetContainersCount: 13,
    screens: [
      'dashboard',
      'journals',
      'budgets',
      'customAccountItems',
      'customSections',
      'customSegments',
      'members',
      'logs',
      'settings',
    ],
  },
  settlement: {
    label: '決算支援',
    maxBudgetContainersCount: 13,
    screens: [
      'dashboard',
      'journals',
      'journalsByTax',
      'accountItemSettings',
      'periodComparison',
      'monthlyTrends',
      'monthlyPartnerBalances',
      'twoDimensions',
      'customAccountItems',
      'customJournalsCheckRules',
      'customPartners',
      'journalsCheck',
      'trialsCheck',
      'controlReports',
      'members',
      'logs',
      'settings',
    ],
  },
  basic: {
    label: 'freee経営管理スタータープラン',
    maxBudgetContainersCount: 1,
    // NOTE: カスタム部門は並び替えのみ可能（作成・編集・削除は不可）。
    readonlyCustomSections: true,
    // NOTE: 月次推移は網羅性チェックのみ可能（数値項目は「当期実績」のみに制限する）。
    monthlyTrendsCheckOnly: true,
    // NOTE: 見込機能は利用不可（スタンダード・アドバンスのみ）。
    forecastDisabled: true,
    screens: [
      'dashboard',
      'journals',
      'accountItemSettings',
      'budgets',
      'customSections',
      'monthlyTrends',
      'customJournalsCheckRules',
      'journalsCheck',
      'trialsCheck',
      'members',
      'settings',
    ],
  },
  standard: {
    label: 'freee経営管理スタンダードプラン',
    maxBudgetContainersCount: 13,
    // NOTE: カスタム部門は並び替えのみ可能（作成・編集・削除は不可）。
    readonlyCustomSections: true,
    screens: [
      'dashboard',
      'journals',
      'journalsByTax',
      'accountItemSettings',
      'periodComparison',
      'monthlyTrends',
      'twoDimensions',
      'budgets',
      'customSections',
      'customJournalsCheckRules',
      'journalsCheck',
      'trialsCheck',
      'members',
      'settings',
    ],
  },
  advanced: {
    label: 'freee経営管理アドバンスプラン',
    maxBudgetContainersCount: 13,
    screens: [
      'dashboard',
      'journals',
      'journalsByTax',
      'accountItemSettings',
      'periodComparison',
      'monthlyTrends',
      'monthlyPartnerBalances',
      'twoDimensions',
      'budgets',
      'customAccountItems',
      'customSections',
      'customSegments',
      'customJournalsCheckRules',
      'customPartners',
      'journalsCheck',
      'trialsCheck',
      'controlReports',
      'members',
      'logs',
      'settings',
    ],
  },
};
const features = {
};
const isTrialExpired = (company) => {
  return company.isTrial && leftTrialDays(company) < 0;
};
const canUseFeature = (company, feature) => company?.isTrial || company?.enabledFeatures?.includes(feature);
// NOTE: カスタム部門画面を持つプランのうち、readonlyCustomSectionsでないプランを契約していれば作成・編集・削除が可能。
//       スタータープラン（basic）・スタンダードプラン（standard）のみの契約では並び替えのみ可能。
const canEditCustomSections = (company) => (company?.enabledPlans || []).some(_ => plans[_]?.screens?.includes('customSections') && !plans[_]?.readonlyCustomSections);
// NOTE: 月次推移画面を持つプランのうち、monthlyTrendsCheckOnlyでないプランを契約していれば数値項目をすべて利用可能。
//       スタータープラン（basic）のみの契約では網羅性チェックのみ可能（数値項目は「当期実績」のみ）。
const canUseMonthlyTrendsFull = (company) => (company?.enabledPlans || []).some(_ => plans[_]?.screens?.includes('monthlyTrends') && !plans[_]?.monthlyTrendsCheckOnly);
// NOTE: 見込機能はスタンダード・アドバンスのみ利用可能。forecastDisabledでないプラン（スタータープランbasic以外）を契約していれば利用可能。
//       スタータープラン（basic）のみの契約では見込機能を利用できない。
const canUseForecast = (company) => (company?.enabledPlans || []).some(_ => plans[_] && !plans[_]?.forecastDisabled);
const usedSegments = (company) => {
  const usesSegment1 = company.usesSegment1;
  const usesSegment2And3 = company.usesSegment2And3;
  return [
    usesSegment1 && 1,
    usesSegment2And3 && 2,
    usesSegment2And3 && 3,
  ].filter(_ => _);
};
const dimensionsOfCompany = (company) => (
  omit(allDimensions, [...(!usedSegments(company).includes(1) ? ['segment1s'] : []), ...(!usedSegments(company).includes(2) ? ['segment2s', 'segment3s'] : [])])
);
const enabledBudgetSubjectTypes = (company) => {
  return pick(budgetSubjectTypes, ['section', usedSegments(company).includes(1) && 'segment1', ...(usedSegments(company).includes(2) ? ['segment2', 'segment3'] : [])]);
};
const generalSettingsFields = (company, accountItems) => {
  const bsAccountItems = accountItems.filter(_ => accountItemCategoriesByName[_.account_category]?.type === 'bs');
  return {
    disablesCommentFiles: {
      label: 'コメントのファイル添付不可',
      type: 'boolean',
      initialValue: false,
    },
    disablesBudgetsShare: {
      label: '予実共有URL作成不可',
      type: 'boolean',
      initialValue: false,
    },
    usesSegment1: {
      label: 'セグメント1の利用',
      type: 'boolean',
      initialValue: false,
    },
    usesSegment2And3: {
      label: 'セグメント2, 3の利用',
      type: 'boolean',
      initialValue: false,
    },
    usesSlackNotifications: {
      label: 'Slack通知の利用',
      type: 'boolean',
      initialValue: false,
    },
    slackIncomingWebhookUrl: {
      type: 'string',
      label: 'Slack Incoming Webhook URL',
      validations: {
        required: v => !isEmpty(v),
        format: v => v == null || isURL(v),
      },
      hidden: _ => !_.usesSlackNotifications,
    },
    partnerCreditAccountItemIds: {
      label: '与信対象科目',
      type: 'multiSelect',
      options: bsAccountItems.map(_ => ({ label: _.name, value: _.id, })),
    },
    budgetSubjectDefaultDisplayCount: {
      label: '予実部門（セグメント）初期表示数',
      type: 'select',
      options: [5, 10, 15].map(_ => ({ label: _, value: _, })),
      initialValue: 5,
    },
    useForecast: {
      label: '見込の利用',
      type: 'boolean',
      initialValue: false,
      // NOTE: 見込機能はスタンダード・アドバンスのみ。スタータープランのみの契約では設定を非表示。
      hidden: _ => !canUseForecast(company),
    },
  };
};

const fiscalYearFields = ({ company, freeeFiscalYears, }) => {
  const lastYear = parseInt(last(sortBy(freeeFiscalYears.map(_ => _.end_date), _ => _)).slice(0, 4), 10);
  return {
    start_date: {
      label: '開始',
      type: 'date',
      selector: true,
      yearRange: [lastYear, lastYear + 5],
      inputProps: { onlyYearMonth: true },
      initialValue: null,
    },
    end_date: {
      label: '終了',
      type: 'date',
      selector: true,
      yearRange: [lastYear, lastYear + 5],
      inputProps: { onlyYearMonth: true },
      initialValue: null,
    },
  };
};

module.exports = {
  adminFields: () => {
    return {
      trialDays: {
        label: '試用期間(日)',
        type: 'integer',
        validations: {
          greaterThanOrEqualTo0: v => v != null && v >= 0,
        },
        initialValue: 30,
      },
      licenseCount: {
        label: 'ライセンス数',
        type: 'integer',
        validations: {
          required: v => v != null,
          greaterThanOrEqualTo0: v => v == null || v >= 0,
        },
        initialValue: 10,
      },
      isTrial: {
        label: 'トライアル',
        type: 'boolean',
        initialValue: true,
      },
      enabledPlans: {
        label: 'プラン',
        type: 'multiSelect',
        options: entries(plans).map(([k, { label }]) => ({ label, value: k })),
        initialValue: ['advanced'],
      },
      enabledFeatures: {
        label: 'オプション',
        type: 'multiSelect',
        options: entries(features).map(([k, v]) => ({ label: v.label, value: k, })),
      },
      shouldDelete: {
        label: '削除する',
        type: 'boolean',
        hidden: _ => !_.isTrial,
        initialValue: false,
      },
    };
  },
  generalSettingsFields,
  plans,
  features,
  isTrialExpired,
  usedSegments,
  enabledBudgetSubjectTypes,
  dimensionsOfCompany,
  canUseFeature,
  canEditCustomSections,
  canUseMonthlyTrendsFull,
  canUseForecast,
  fiscalYearFields,
};
